import mustache from "mustache";
import { BaseLoginResponse } from "../../../../base/response/BaseLoginResponse";
import { IConnectRender } from "../../../definitions/IConnectRender";
import template from "./loginTemplate.html";
import { FormCardRender } from "../../../implementations/card/FormCardRender";
import {
  fx_createLoginByEmailPasswordRequest,
  LoginByEmailPasswordRequest,
} from "../../../../modulos/seguridad/login/request/LoginByEmailPasswordRequest";
import { LoginByEmailPasswordGrpcService } from "../../../../modulos/seguridad/login/services/LoginByEmailPasswordGrpcService";
import { TextField } from "../../../implementations/form/text/TextField";
import { TextConfig } from "../../../definitions/form/inputs/config/TextConfig";
import { RecuperarPasswordFlowRender } from "../password/form/RecuperarPasswordFlowRender";

mustache.parse(template);

function createLoginEmail(
  request: LoginByEmailPasswordRequest,
): TextField<LoginByEmailPasswordRequest> {
  const config: TextConfig<LoginByEmailPasswordRequest> = {
    get(request) {
      return request.email;
    },
    set(request, newValue) {
      request.email = newValue;
    },
    label: "Email",
    type: "email",
    description: "Email de ingreso al sistema",
    icon: "ph-envelope",
    autocomplete: "email",
  };
  return new TextField<LoginByEmailPasswordRequest>(request, config, {
    isRequired: true,
  });
}

function createLoginPassword(
  request: LoginByEmailPasswordRequest,
): TextField<LoginByEmailPasswordRequest> {
  const config: TextConfig<LoginByEmailPasswordRequest> = {
    get(request) {
      return request.password;
    },
    set(request, newValue) {
      request.password = newValue;
    },
    label: "Password",
    type: "password",
    description: "Password de ingreso al sistema",
    icon: "ph-shield-check",
    autocomplete: "current-password",
  };
  return new TextField<LoginByEmailPasswordRequest>(request, config, {
    isRequired: true,
    minLength: 6,
  });
}

function createLoginFormCard<TLoginResponse extends BaseLoginResponse>(
  request: LoginByEmailPasswordRequest,
  onLoginEnd: (response: TLoginResponse) => void,
) {
  return new FormCardRender<LoginByEmailPasswordRequest, TLoginResponse>(
    request,
    {
      cardHeader: {
        title: "Ingreso al Sistema",
      },
      createFormFields(request) {
        return [createLoginEmail(request), createLoginPassword(request)];
      },
      onSubmitEnd: onLoginEnd,
      saveService: new LoginByEmailPasswordGrpcService<TLoginResponse>(),
      submitText: "Ingresar",
    },
  );
}

/** Pantalla de login. Aloja dos contenidos intercambiables en el mismo lugar:
 *  el formulario de ingreso y el flujo de recuperación de contraseña
 *  (`RecuperarPasswordFlowRender`), sin rutas nuevas ni deep links. */
export class Login<
  TLoginResponse extends BaseLoginResponse,
> implements IConnectRender {
  private readonly _logoRoute: string;
  /** Si esta marca expone el acceso a la recuperación de contraseña. */
  private readonly _withRecuperarPassword: boolean;
  private readonly _onLoginEnd: (response: TLoginResponse) => void;
  /** Request del ingreso, vivo mientras dure la pantalla: así lo escrito
   *  sobrevive al ida y vuelta por el flujo de recuperación. */
  private readonly _request: LoginByEmailPasswordRequest;

  constructor(
    logoRoute: string,
    withRecuperarPassword: boolean,
    onLoginEnd: (response: TLoginResponse) => void,
  ) {
    this._logoRoute = logoRoute;
    this._withRecuperarPassword = withRecuperarPassword;
    this._onLoginEnd = onLoginEnd;
    this._request = fx_createLoginByEmailPasswordRequest();
  }

  /** Arma el marco; los wrappers los llena `connect()` — el contenido cambia
   *  (ingreso ↔ recuperación) y así hay un único camino de montaje. */
  render(): string {
    return mustache.render(template, {
      logoRoute: this._logoRoute,
      loginForm: "",
      bottomContent: "",
    });
  }

  connect(): void {
    this._formWrapper = document.getElementById("loginFormWrapper");
    this._bottomWrapper = document.getElementById("loginBottomWrapper");
    this._showLogin();
  }

  dispose(): void {
    this._disposeCurrent();
    this._formWrapper = null;
    this._bottomWrapper = null;
  }

  private _formWrapper: HTMLElement | null = null;
  private _bottomWrapper: HTMLElement | null = null;
  private _current: IConnectRender | null = null;
  private _forgotButton: HTMLButtonElement | null = null;

  /** Muestra el formulario de ingreso y el acceso a la recuperación. */
  private _showLogin(): void {
    this._mount(
      createLoginFormCard<TLoginResponse>(this._request, this._onLoginEnd),
    );

    // Sin recuperación de contraseña la zona inferior queda vacía: el flujo
    // sigue existiendo, pero esta marca no lo expone.
    if (!this._withRecuperarPassword || !this._bottomWrapper) return;
    this._bottomWrapper.innerHTML = `<button class="sx-btn sx-btn-flat sx-btn-block" type="button" id="loginForgot">¿Olvidaste tu contraseña?</button>`;
    this._forgotButton = document.getElementById(
      "loginForgot",
    ) as HTMLButtonElement;
    this._forgotButton.addEventListener("click", this._onForgotClick);
  }

  /** Muestra el flujo de recuperación en lugar del formulario de ingreso. */
  private _showRecuperar(): void {
    this._mount(
      new RecuperarPasswordFlowRender({
        email: this._request.email,
        onCancel: () => this._showLogin(),
        onRestablecido: (email) => {
          // La contraseña vieja ya no sirve: se limpia y se deja el email listo
          // para ingresar con la nueva.
          this._request.email = email;
          this._request.password = "";
          this._showLogin();
        },
      }),
    );
  }

  private _onForgotClick = (): void => {
    this._showRecuperar();
  };

  /** Reemplaza el contenido del wrapper, disponiendo lo anterior. */
  private _mount(render: IConnectRender): void {
    if (!this._formWrapper) return;

    this._disposeCurrent();
    this._current = render;
    this._formWrapper.innerHTML = render.render();
    render.connect();
  }

  private _disposeCurrent(): void {
    this._forgotButton?.removeEventListener("click", this._onForgotClick);
    this._forgotButton = null;
    if (this._bottomWrapper) this._bottomWrapper.innerHTML = "";

    this._current?.dispose();
    this._current = null;
  }
}
